import type { CSSProperties } from 'react';
import type { RenewalRobotAction } from '@bhops/shared';

// 재계약 — 옛 고객사 로봇마다 회수 · 이어서 사용(2026-10-22 오너 — "회수 후 다시 설치인데, 낮은 확률로
// 기존 로봇을 이어서 쓸 가능성도 있음"). 일정 추가 창과 [재계약 완료] 창이 같이 쓴다. 기본은 회수.

export interface RenewalRobotRow {
  robotId: string;
  label: string;
  modelName: string | null;
}

const ACTIONS: Array<{ value: RenewalRobotAction; label: string }> = [
  { value: 'RETURN', label: '회수' },
  { value: 'CARRY', label: '이어서 사용' },
];

export function RenewalPlanList({
  robots,
  plan,
  onChange,
  disabled = false,
  testId = 'renewal-plan',
}: {
  robots: RenewalRobotRow[];
  /** robotId → 처리. 없으면 회수. */
  plan: Record<string, RenewalRobotAction>;
  onChange: (robotId: string, action: RenewalRobotAction) => void;
  disabled?: boolean;
  testId?: string;
}) {
  if (robots.length === 0)
    return (
      <span style={hint} data-testid={`${testId}-empty`}>
        옛 고객사에 나가 있는 로봇이 없습니다.
      </span>
    );
  return (
    <div style={list} data-testid={testId}>
      {robots.map((r) => {
        const current = plan[r.robotId] ?? 'RETURN';
        return (
          <div key={r.robotId} style={row}>
            <span style={robotSt}>
              {r.label}
              {r.modelName ? ` · ${r.modelName}` : ''}
            </span>
            <div role="radiogroup" aria-label={`${r.label} 처리`} style={seg}>
              {ACTIONS.map((a) => {
                const on = current === a.value;
                return (
                  <button
                    key={a.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    disabled={disabled}
                    onClick={() => onChange(r.robotId, a.value)}
                    data-testid={`${testId}-${r.robotId}-${a.value}`}
                    style={{
                      ...segBtn,
                      background: on
                        ? a.value === 'CARRY'
                          ? 'var(--cal-renewal-tint)'
                          : 'var(--cal-return-tint)'
                        : 'transparent',
                      color: on
                        ? a.value === 'CARRY'
                          ? 'var(--cal-renewal)'
                          : 'var(--cal-return)'
                        : 'var(--fg-muted)',
                      fontWeight: on ? 700 : 500,
                    }}
                  >
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const list: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  maxHeight: 200,
  overflowY: 'auto',
  padding: '6px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
};
const row: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  fontSize: 13,
  padding: '2px 0',
};
const robotSt: CSSProperties = { fontFamily: 'var(--font-mono)', minWidth: 0 };
const seg: CSSProperties = {
  display: 'inline-flex',
  flexShrink: 0,
  border: '1px solid var(--border-default)',
  borderRadius: 999,
  overflow: 'hidden',
};
const segBtn: CSSProperties = {
  border: 'none',
  padding: '4px 10px',
  fontSize: 12,
  cursor: 'pointer',
};
const hint: CSSProperties = { fontSize: 12, color: 'var(--fg-muted)' };
